import React, { useState, useEffect } from 'react';
import { Search, Play, Clock, FileText, TrendingUp, AlertCircle, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { scrapingService } from '../services/scrapingService';
import { shouldSuppressRateLimit } from '../utils/httpErrors';

const ScrapingPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [scrapingResults, setScrapingResults] = useState([]);
  const [pubblicitalegaleTaskId, setPubblicitalegaleTaskId] = useState(null);
  const [pubblicitalegaleStatus, setPubblicitalegaleStatus] = useState(null);

  // Carica i risultati di scraping reali dal backend
  useEffect(() => {
    loadScrapingResults();
  }, []);

  const loadScrapingResults = async () => {
    try {
      const results = await scrapingService.getScrapingResults();
      setScrapingResults(results);
    } catch (error) {
      console.error('Errore nel caricamento risultati:', error);
      // Fallback con dati di esempio se l'API non è disponibile
      setScrapingResults([
        {
          id: 1,
          status: 'completed',
          total_items: 15,
          created_at: '2025-08-21T14:00:00Z',
          meta_data: { keywords: ['manutenzione'], regions: ['Lazio'] }
        }
      ]);
    }
  };

  const handleViewLotti = async (scrapingResultId) => {
    try {
      const lotti = await scrapingService.getLotti(scrapingResultId);
      console.log('Lotti trovati:', lotti);
      
      // Per ora mostriamo solo un toast, in futuro potremmo aprire un modal
      toast.success(`Caricati ${lotti.length} lotti per questo job di scraping`);
      
      // TODO: Implementare modal per visualizzare i lotti
      // setSelectedLotti(lotti);
    } catch (error) {
      console.error('Errore nel caricamento lotti:', error);
      if (shouldSuppressRateLimit(error)) {
        return;
      }
      toast.error('Errore nel caricamento dei lotti');
    }
  };

  const pollTask = async (taskId) => {
    try {
      const status = await scrapingService.getTaskStatus(taskId);
      setPubblicitalegaleStatus(status);

      if (status?.state === 'SUCCESS') {
        toast.success('✅ Pubblicità Legale: completato');
        return true;
      }
      if (status?.state === 'FAILURE') {
        toast.error(`❌ Pubblicità Legale: errore (${status?.error || 'unknown'})`);
        return true;
      }
      return false;
    } catch (e) {
      console.error('Errore polling task:', e);
      return false;
    }
  };

  const startPubblicitalegale = async () => {
    setIsLoading(true);
    setPubblicitalegaleStatus(null);
    try {
      const resp = await scrapingService.startPubblicitalegaleDiscover();
      const taskId = resp?.task_id;
      if (!taskId) {
        throw new Error('task_id mancante nella risposta');
      }
      setPubblicitalegaleTaskId(taskId);
      toast.success('🚀 Pubblicità Legale: task avviato');

      // Polling leggero (ogni 3s) finché non finisce
      const interval = setInterval(async () => {
        const done = await pollTask(taskId);
        if (done) {
          clearInterval(interval);
          // ricarica eventuali risultati DB (se usati altrove)
          loadScrapingResults();
        }
      }, 3000);
    } catch (error) {
      console.error('Errore avvio Pubblicità Legale:', error);
      if (shouldSuppressRateLimit(error)) {
        return;
      }
      toast.error(error?.response?.data?.detail || error?.message || 'Errore avvio task');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'running': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <TrendingUp className="h-4 w-4" />;
      case 'running': return <Clock className="h-4 w-4" />;
      case 'failed': return <AlertCircle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Search className="h-6 w-6 text-primary-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">
                Scraping Gare d'Appalto
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => loadScrapingResults()}
                className="btn-secondary flex items-center space-x-2"
                title="Aggiorna risultati"
              >
                <Search className="h-4 w-4" />
                <span>Aggiorna</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0 space-y-6">
          
          {/* Pubblicità Legale */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Pubblicità Legale (ANAC) — Scrape + Ingest
            </h2>

            <div className="space-y-4">
              <button
                type="button"
                disabled={isLoading}
                onClick={startPubblicitalegale}
                className="btn-primary flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Avvio task...</span>
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    <span>Avvia Pubblicità Legale</span>
                  </>
                )}
              </button>

              {pubblicitalegaleTaskId && (
                <div className="text-sm text-gray-700">
                  <strong>Task ID:</strong> {pubblicitalegaleTaskId}
                </div>
              )}

              {pubblicitalegaleStatus && (
                <div className="text-sm text-gray-700">
                  <strong>Stato:</strong> {pubblicitalegaleStatus.state}
                  {pubblicitalegaleStatus.error && (
                    <div className="text-red-600">
                      <strong>Errore:</strong> {pubblicitalegaleStatus.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Lista risultati scraping */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Risultati Job di Scraping
            </h2>
            
            {scrapingResults.length === 0 ? (
              <div className="text-center py-8">
                <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nessun job di scraping eseguito</p>
              </div>
            ) : (
              <div className="space-y-4">
                {scrapingResults.map((result) => (
                  <div
                    key={result.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(result.status)}`}>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(result.status)}
                            <span className="capitalize">{result.status}</span>
                          </div>
                        </div>
                        
                        <div>
                          <p className="font-medium text-gray-900">
                            Job #{result.id}
                          </p>
                          <p className="text-sm text-gray-500">
                            {result.total_items || 0} elementi trovati
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            {new Date(result.created_at).toLocaleDateString('it-IT')}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(result.created_at).toLocaleTimeString('it-IT')}
                          </p>
                        </div>
                        
                        {result.status === 'completed' && result.total_items > 0 && (
                          <button
                            onClick={() => handleViewLotti(result.id)}
                            className="btn-secondary flex items-center space-x-1"
                            title="Visualizza lotti trovati"
                          >
                            <Eye className="h-4 w-4" />
                            <span>Visualizza</span>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Metadata del job */}
                    {result.meta_data && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          {result.meta_data.keywords && (
                            <span>
                              <strong>Keywords:</strong> {
                                Array.isArray(result.meta_data.keywords) 
                                  ? result.meta_data.keywords.join(', ')
                                  : result.meta_data.keywords
                              }
                            </span>
                          )}
                          {result.meta_data.regions && (
                            <span>
                              <strong>Regioni:</strong> {
                                Array.isArray(result.meta_data.regions)
                                  ? result.meta_data.regions.join(', ')
                                  : result.meta_data.regions
                              }
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ScrapingPage;
